'use client';

import { customInputProps } from '@/app/entrar/customInputProps';
import {
  FloatingLabelPasswordInput,
  FloatingLabelTextInput,
} from '@/components/FloatingLabelComponents/FloatingLabelInput';
import useApi from '@/hooks/useApi/useApi';
import { useStore } from '@/stores/store';
import { User } from '@/typings/user';
import { setAxiosAuthentication } from '@/utils/axios';
import { shouldSkipLoggingForAuthError } from '@/utils/error';
import { USER_TOKEN } from '@/utils/storageKeys';
import { Alert, Anchor, Button, Group, Text, Title } from '@mantine/core';
import { isEmail, isNotEmpty, useForm } from '@mantine/form';
import { IconArrowLeft } from '@tabler/icons-react';
import { deleteCookie, setCookie } from 'cookies-next';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';
import { LoginFormInput } from '../LoginByEmail/LoginByEmail';

type Props = {
  initialValues?: Partial<LoginFormInput>;
  defaultFocus?: keyof LoginFormInput;
  onGoBack: (value?: string) => void;
};

export default function FirstAccess({ onGoBack, initialValues, defaultFocus }: Props) {
  const { put, loading } = useApi<{ email: string; password: string }>('createPassword');
  const { get: getAuthUser } = useApi('me');
  const router = useRouter();
  const updateData = useStore((store) => store.updateData);
  const setOrganization = useStore((store) => store.setSelectedOrganization);

  const [errorNode, setErrorNode] = useState<ReactNode>();

  const form = useForm<LoginFormInput>({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      ...initialValues,
    },
    validate: {
      password: isNotEmpty('Insira uma senha'),
      confirmPassword: (value, values) =>
        value === values.password ? undefined : 'As senhas precisam ser iguais',
      email: isEmail('Você precisa digitar um e-mail válido'),
    },
    validateInputOnBlur: true,
  });

  const handleSubmit = async ({ password, ...values }: LoginFormInput) => {
    setErrorNode(undefined);
    put<{ token: string }>(
      { password, email: values.email },
      {
        error: {
          message: 'Ocorreu um erro ao criar a senha. Tente novamente',
          showNotification: false,
          shouldSkipLogging: shouldSkipLoggingForAuthError,
        },
        onSuccess: ({ token }) => {
          localStorage.setItem(USER_TOKEN, token);
          const maxAge = 1000 * 60 * 60 * 24 * 365; // 1 year for now
          setCookie(USER_TOKEN, token, { maxAge });
          setAxiosAuthentication(token);
          getAuthUser<User>({
            error: {
              message: 'Error on logging user after creating password',
              showNotification: false,
            },
            onSuccess: async (user) => {
              updateData('user', () => user);
              const org = user.organizations?.[0]?.organization;
              if (org) {
                setOrganization(org);
              }
              const path = '/';
              await fetch(`/api/revalidate?path=${encodeURIComponent(path)}&layout=true`);
              router.push('/');
              router.refresh();
            },
            onError: () => {
              deleteCookie(USER_TOKEN);
              localStorage.removeItem(USER_TOKEN);
              setErrorNode(
                <Alert
                  color="red"
                  radius="md"
                  p="sm"
                  classNames={{ message: 'text-center text-white' }}
                >
                  Sua senha foi registrada com sucesso! Porém, não conseguimos realizar sua
                  autenticação ainda. Clique{' '}
                  <Anchor
                    component="button"
                    c="red"
                    fw="bold"
                    onClick={() => onGoBack(values['email'])}
                  >
                    aqui
                  </Anchor>{' '}
                  para entrar
                </Alert>,
              );
            },
          });
        },
        onError: (error) => {
          if (error?.message.includes('User not found')) {
            setErrorNode(
              <Alert
                color="red"
                radius="md"
                p="sm"
                classNames={{ message: 'text-center text-white' }}
              >
                Usuário não encontrado. Entre em contato com o administrador do sistema
              </Alert>,
            );
          } else if (error?.message.includes('User already has a password')) {
            setErrorNode(
              <Alert
                color="red"
                radius="md"
                p="sm"
                classNames={{ message: 'text-center text-white' }}
              >
                <Text c="light">
                  Você já se registrou anteriormente. Clique{' '}
                  <Anchor
                    component="button"
                    c="red"
                    fw="bold"
                    onClick={() => onGoBack(form.values.email)}
                  >
                    aqui
                  </Anchor>{' '}
                  para entrar
                </Text>
              </Alert>,
            );
          } else {
            setErrorNode(
              <Alert
                color="red"
                radius="md"
                p="sm"
                classNames={{ message: 'text-center text-white' }}
              >
                Ocorreu um erro ao criar a senha. Tente novamente
              </Alert>,
            );
          }
        },
      },
    );
  };

  useEffect(() => {
    if (defaultFocus) {
      const input = form.getInputNode(defaultFocus);
      if (input) {
        input.focus();
      }
    }
  }, [defaultFocus]);

  return (
    <form className="flex h-full flex-col text-white" onSubmit={form.onSubmit(handleSubmit)}>
      <div>
        <Title order={1} ta="center" mb="sm">
          Primeiro acesso
        </Title>
        <Text ta="center" mb="lg">
          Como este é seu primeiro acesso, informe seu email (por onde foi convidado) e crie uma
          senha de acesso.
        </Text>
      </div>
      <div className="flex flex-col gap-4 animate-[fade-in_0.5s_ease-in]">
        <FloatingLabelTextInput
          {...customInputProps}
          {...form.getInputProps('email')}
          type="email"
          label="E-mail"
          autoComplete="on"
        />

        <FloatingLabelPasswordInput
          {...customInputProps}
          {...form.getInputProps('password')}
          label="Crie uma senha"
        />
        <FloatingLabelPasswordInput
          {...customInputProps}
          {...form.getInputProps('confirmPassword')}
          label="Confirmar senha"
        />

        {errorNode}

        <Group justify="space-between" align="center" mt="md">
          <Button
            component={Link}
            variant="subtle"
            color="white"
            leftSection={<IconArrowLeft size={18} stroke={1.5} />}
            href={`/entrar${
              form.values.email ? `?email=${encodeURIComponent(form.values.email)}` : ''
            }`}
          >
            Voltar
          </Button>

          <Button type="submit" variant="filled" loading={loading}>
            Criar conta
          </Button>
        </Group>
      </div>
    </form>
  );
}
